/**
 * sharedClock — one lazy, reference-counted timer per distinct cadence (#333).
 *
 * Replaces `useNow.ts` (1s) and `fastTick.ts` (250ms), which were the same
 * pattern written twice at two hard-coded rates. Display-only clocks — the
 * ones that exist solely to re-render a relative age or a freshness label —
 * subscribe here instead of each calling `setInterval` on mount. Ten call
 * sites across the app collapse onto three timers, and a timer only exists
 * while at least one subscriber is mounted.
 *
 * NOT for timers with side effects. A tick that samples a value, writes
 * state, or drives alert timing owns its own interval: its cadence is data,
 * not presentation, and sharing it would couple unrelated surfaces. See the
 * inventory on #333 for the excluded sites and why.
 *
 * Three things here are load-bearing:
 *
 *   FIRST RENDER. `useSyncExternalStore` requires a `getSnapshot` that returns
 *   the same value between ticks — it cannot return a live `Date.now()` or
 *   React re-renders forever. So a component mounting mid-cadence would read
 *   whatever the last tick wrote, up to one full cadence stale. Invisible at
 *   1s; a visible lie at 5s. Hence `subscribe` stamps `now` fresh and notifies
 *   the existing listeners: one extra render per mount buys an honest first
 *   paint. (Quantising the snapshot to cadence boundaries would also be stable,
 *   but it under-reports every age by up to a cadence — it changes displayed
 *   numbers, so it was rejected.)
 *
 *   WAKE. Browsers clamp and eventually throttle background intervals, so a
 *   tab restored after a few minutes would otherwise show ages frozen at
 *   whatever the last throttled tick wrote. `onTabVisible` re-stamps `now` the
 *   moment the tab comes back. Its teardown is paired with `clearInterval` —
 *   dropping one and not the other leaks a document listener per cadence.
 *
 *   REGISTRIES ARE NEVER DELETED. `getSnapshot` runs during render and must
 *   not conditionally create objects; keeping the registry alive keeps the
 *   snapshot stable and lets a re-subscribe reuse the last `now`. Only the
 *   interval starts and stops. The map is bounded by the number of distinct
 *   cadences in the app, which is three.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { onTabVisible } from '@/lib/visibilityThrottle';

interface Registry {
  /** `Date.now()` as of the most recent tick. */
  now: number;
  listeners: Set<() => void>;
  intervalId: ReturnType<typeof setInterval> | null;
  /** Teardown for this cadence's visibility listener; paired with intervalId. */
  stopVisibility: (() => void) | null;
}

const registries = new Map<number, Registry>();

function getRegistry(cadenceMs: number): Registry {
  let reg = registries.get(cadenceMs);
  if (reg == null) {
    reg = { now: Date.now(), listeners: new Set(), intervalId: null, stopVisibility: null };
    registries.set(cadenceMs, reg);
  }
  return reg;
}

/** Stamp the clock and fan out. Copy the set: a listener may unsubscribe
 *  during its own notification (React does this on unmount). */
function advance(reg: Registry): void {
  reg.now = Date.now();
  for (const listener of [...reg.listeners]) listener();
}

/**
 * Subscribe to the shared tick at `cadenceMs`. Returns the unsubscribe.
 *
 * Imperative rather than hook-only on purpose: consumers that subscribe
 * conditionally (only while a value is still decaying — see `useConsensusGlow`)
 * need to attach and detach outside React's hook rules.
 */
export function subscribeSharedTick(cadenceMs: number, cb: () => void): () => void {
  const reg = getRegistry(cadenceMs);

  // Fresh on every (re)subscribe, so a late mount never inherits a stale tick.
  const previous = reg.now;
  reg.now = Date.now();

  if (reg.intervalId == null) {
    reg.intervalId = setInterval(() => advance(reg), cadenceMs);
    reg.stopVisibility = onTabVisible(() => advance(reg));
  } else if (reg.now !== previous) {
    // The stamp moved the clock for everyone already subscribed, so they need
    // to hear about it or two rows at the same cadence would show different
    // ages until the next tick.
    //
    // Guarded on an actual change because mounting is bursty: thirty rows
    // appearing in one frame all stamp the same millisecond, and notifying on
    // every one of them would be O(n²) callbacks for zero visible difference.
    for (const listener of [...reg.listeners]) listener();
  }

  reg.listeners.add(cb);

  return () => {
    reg.listeners.delete(cb);
    if (reg.listeners.size > 0) return;
    if (reg.intervalId != null) clearInterval(reg.intervalId);
    reg.intervalId = null;
    reg.stopVisibility?.();
    reg.stopVisibility = null;
  };
}

/**
 * `Date.now()` as of the most recent shared tick at `cadenceMs`.
 *
 * `enabled: false` reads the clock without subscribing — the value stops
 * advancing and the component stops re-rendering, but it still renders a
 * sensible time. Flipping back to true re-stamps `now` immediately, which is
 * how a conditionally-ticking consumer gets its instant refresh on re-arm.
 */
export function useSharedNow(cadenceMs = 1000, enabled = true): number {
  const subscribe = useCallback(
    (cb: () => void) => (enabled ? subscribeSharedTick(cadenceMs, cb) : () => {}),
    [cadenceMs, enabled],
  );
  const getSnapshot = useCallback(() => getRegistry(cadenceMs).now, [cadenceMs]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** The 1s clock — countdowns, age labels, freshness counters. */
export function useNow(): number {
  return useSharedNow(1000);
}

/** 4 Hz, for decay animations where a 1s step reads as chunky. */
export const FAST_TICK_MS = 250;

/** Subscribe to the shared 4 Hz tick. Returns the unsubscribe. */
export function subscribeFastTick(cb: () => void): () => void {
  return subscribeSharedTick(FAST_TICK_MS, cb);
}

/** Test-only. Drops every registry so module state can't leak across cases. */
export function __resetSharedClockForTests(): void {
  for (const reg of registries.values()) {
    if (reg.intervalId != null) clearInterval(reg.intervalId);
    reg.stopVisibility?.();
  }
  registries.clear();
}
